// Customizable Area Start
import React, { Component } from "react";

interface MicLevelIconProps {
  sensitivity?: number;
  muted?: boolean;
}

interface MicLevelIconState {
  volumeLevel: number;
}

class MicLevelIcon extends Component<MicLevelIconProps, MicLevelIconState> {
  state: MicLevelIconState = {
    volumeLevel: 0,
  };

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private animationFrameId: number | null = null;

  componentDidMount() {
    this.startMicMonitoring();
  }

  componentDidUpdate(prevProps: MicLevelIconProps) {
    if (prevProps.muted !== this.props.muted) {
      if (this.props.muted) {
        this.pauseMonitoring();
      } else {
        this.resumeMonitoring();
      }
    }
  }

  componentWillUnmount() {
    this.cleanup();
  }

  private async startMicMonitoring() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass();
      const mic = this.audioContext.createMediaStreamSource(stream);

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      mic.connect(this.analyser);

      if (!this.props.muted) {
        this.monitor();
      }
    } catch (error) {
      console.error("Microphone access error:", error);
    }
  }

  private monitor = () => {
    if (!this.analyser || !this.dataArray || this.props.muted) return;

    this.analyser.getByteFrequencyData(this.dataArray);
    const avg =
      this.dataArray.reduce((a, b) => a + b, 0) / this.dataArray.length;

    const { sensitivity = 2 } = this.props;
    const normalized = Math.max(0, avg - sensitivity) / 100;
    const volumeLevel = Math.min(normalized, 1);

    this.setState({ volumeLevel });

    this.animationFrameId = requestAnimationFrame(this.monitor);
  };

  private pauseMonitoring() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.setState({ volumeLevel: 0 });
  }

  private resumeMonitoring() {
    if (!this.animationFrameId) {
      this.monitor();
    }
  }

  private cleanup() {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    if (this.audioContext) this.audioContext.close();
  }

  render() {
    const { volumeLevel } = this.state;
    const { muted } = this.props;
    const clipHeight = 16 * (1 - volumeLevel);

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
        viewBox="0 0 16 16"
        fill="none"
      >
        <defs>
          <clipPath id="micLevelClip">
            <rect x="0" y={clipHeight} width="16" height="16" />
          </clipPath>
        </defs>

        {!muted && (
          <g clipPath="url(#micLevelClip)">
            <path
              d="M10.6673 4.8877H5.56587H5.33398V8.4046L7.42094 10.6655H8.81224L10.6673 8.65581V4.8877Z"
              fill="white"
              opacity="0.8"
            />
          </g>
        )}

        <path
          d="M8.00065 10.333C9.47398 10.333 10.6673 9.13967 10.6673 7.66634V3.99967C10.6673 2.52634 9.47398 1.33301 8.00065 1.33301C6.52732 1.33301 5.33398 2.52634 5.33398 3.99967V7.66634C5.33398 9.13967 6.52732 10.333 8.00065 10.333Z"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.90039 6.43359V7.56693C2.90039 10.3803 5.18706 12.6669 8.00039 12.6669C10.8137 12.6669 13.1004 10.3803 13.1004 7.56693V6.43359"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 12.666V14.666"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {muted && (
          <line
            x1="3"
            y1="2"
            x2="13"
            y2="13"
            stroke="#fff"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        )}
      </svg>
    );
  }
}

export default MicLevelIcon;
// Customizable Area End
